import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../button";
import styles from "./style.module.css";

const BENEFITS = [
  "🚨 Важная информация о статусе работы VPS",
  "⏳ Напоминания перед окончанием подписки",
  "✨ Информация о новых функциях",
] as const;

interface LinkTelegramModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  error?: string | null;
}

/**
 * Подтверждение привязки Telegram (web).
 * Mobile: bottom sheet; desktop (768px+): centered 440px dialog.
 */
function LinkTelegramModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  error = null,
}: LinkTelegramModalProps) {
  const [animatedOpen, setAnimatedOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setAnimatedOpen(false);
      return;
    }
    const frame = requestAnimationFrame(() => setAnimatedOpen(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`${styles.overlay} ${animatedOpen ? styles.open : ""}`}
      role="presentation"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-telegram-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 id="link-telegram-modal-title" className={styles.title}>
            Привяжите Telegram
          </h2>
          <p className={styles.description}>
            Перенесем вашу подписку сюда, если вы уже клиент нашего Telegram-бота.
            Если еще нет – управлять подпиской будет удобнее.
          </p>
        </div>
        <ul className={styles.benefits}>
          {BENEFITS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.actions}>
          <Button
            label="Привязать"
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          />
          <Button
            label="В следующий раз"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LinkTelegramModal;
